import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp, TrendingDown, Package, Users, Star, AlertTriangle,
  DollarSign, Activity, Clock
} from 'lucide-react';
import { api } from '@/lib/api';

export default function CEODashboardPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      const res = await api.get('/api/ceo-dashboard/summary');

      if (res.data?.success) {
        setData(res.data.data || {});
      } else if (res.data) {
        setData(res.data);
      }
    } catch (error) {
      console.error('Error loading CEO dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) =>
    (Number(value) || 0).toLocaleString('ar-EG', {
      style: 'currency',
      currency: 'EGP',
      maximumFractionDigits: 0
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const operations = data?.operations || {};
  const delegates = data?.delegates || {};
  const inventory = data?.inventory || {};
  const trends = Array.isArray(data?.trends) ? data.trends : [];

  /*
   * API currently returns:
   *
   * delegates: {
   *   total: 2,
   *   active: 2,
   *   top: [
   *     { delegateId: 16, shipments: 1 },
   *     { delegateId: 17, shipments: 1 }
   *   ]
   * }
   *
   * Normalize it for the existing UI.
   */
  const topDelegates = Array.isArray(delegates.top)
    ? delegates.top.map((delegate: any) => ({
        ...delegate,
        completedShipments:
          delegate.completedShipments ??
          delegate.shipments ??
          0,
        totalRevenue:
          delegate.totalRevenue ??
          0,
        name:
          delegate.name ||
          `مندوب #${delegate.delegateId}`
      }))
    : [];

  const maxDelegateShipments = Math.max(
    ...topDelegates.map((d: any) => d.completedShipments || 0),
    1
  );

  const maxTrendShipments = Math.max(
    ...trends.map((t: any) => t.shipments || 0),
    1
  );

  const maxTrendRevenue = Math.max(
    ...trends.map((t: any) => t.revenue || 0),
    1
  );

  /*
   * The current API does not expose a dedicated "alerts" object.
   * Use only values that actually exist in the response.
   */
  const delayedShipments = 0;
  const failedDeliveries = operations.returned || 0;
  const lowStockProducts = inventory.lowStock || 0;

  return (
    <div className="p-6 space-y-6" dir="rtl">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            لوحة تحكم الرئيس التنفيذي
          </h1>
          <p className="text-gray-500 mt-1">
            نظرة عامة على أداء الشركة والإحصائيات الرئيسية
          </p>
        </div>

        <button
          onClick={loadData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Activity className="w-4 h-4" />
          تحديث البيانات
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

        {/* Revenue */}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  إجمالي الإيرادات
                </p>

                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(kpis.totalRevenue)}
                </p>

                <div className="flex items-center gap-1 mt-2 text-sm">
                  {(kpis.revenueGrowth || 0) >= 0 ? (
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-500" />
                  )}

                  <span
                    className={
                      (kpis.revenueGrowth || 0) >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }
                  >
                    {kpis.revenueGrowth || 0}% منذ الشهر
                  </span>
                </div>
              </div>

              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shipments */}
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  إجمالي الشحنات
                </p>

                <p className="text-2xl font-bold mt-1">
                  {kpis.totalShipments || 0}
                </p>

                <div className="flex items-center gap-1 mt-2 text-sm">
                  <span className="text-gray-600">
                    {kpis.monthShipments || 0} شحنة هذا الشهر
                  </span>
                </div>
              </div>

              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Delegates */}
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  المندوبون النشطون
                </p>

                <p className="text-2xl font-bold mt-1">
                  {delegates.active ?? kpis.activeDelegates ?? 0}
                </p>

                <div className="flex items-center gap-1 mt-2 text-sm">
                  <Star className="w-4 h-4 text-yellow-500" />

                  <span className="text-gray-600">
                    إجمالي المندوبين: {delegates.total || 0}
                  </span>
                </div>
              </div>

              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Revenue */}
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  إيرادات الشهر
                </p>

                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(kpis.monthRevenue)}
                </p>

                <div className="flex items-center gap-1 mt-2 text-sm">
                  <span className="text-gray-600">
                    من إجمالي {formatCurrency(kpis.totalRevenue)}
                  </span>
                </div>
              </div>

              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operations / Alerts / Delegates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Alerts / Operations */}
        <Card className="lg:col-span-1 border-red-200 bg-red-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-red-700 text-lg">
              <AlertTriangle className="w-5 h-5" />
              تنبيهات العمليات
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">

            {/* Pending */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-red-600" />
                </div>

                <div>
                  <p className="font-semibold text-gray-900">
                    شحنات بانتظار التسليم
                  </p>
                  <p className="text-sm text-gray-500">
                    تحتاج إلى معالجة
                  </p>
                </div>
              </div>

              <Badge className="bg-red-600 text-white text-lg px-3 py-1">
                {operations.pending || 0}
              </Badge>
            </div>

            {/* Out for delivery */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-orange-600" />
                </div>

                <div>
                  <p className="font-semibold text-gray-900">
                    خرجت للتسليم
                  </p>
                  <p className="text-sm text-gray-500">
                    في طريقها إلى العميل
                  </p>
                </div>
              </div>

              <Badge className="bg-orange-600 text-white text-lg px-3 py-1">
                {operations.outForDelivery || 0}
              </Badge>
            </div>

            {/* Returned / Low stock */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Activity className="w-5 h-5 text-yellow-600" />
                </div>

                <div>
                  <p className="font-semibold text-gray-900">
                    منتجات منخفضة المخزون
                  </p>
                  <p className="text-sm text-gray-500">
                    منتجات تحتاج إلى إعادة تخزين
                  </p>
                </div>
              </div>

              <Badge className="bg-yellow-600 text-white text-lg px-3 py-1">
                {lowStockProducts}
              </Badge>
            </div>

            {/* Returned */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100">
              <div>
                <p className="font-semibold text-gray-900">
                  شحنات مرتجعة
                </p>
                <p className="text-sm text-gray-500">
                  إجمالي المرتجعات
                </p>
              </div>

              <Badge className="bg-gray-600 text-white text-lg px-3 py-1">
                {failedDeliveries}
              </Badge>
            </div>

          </CardContent>
        </Card>

        {/* Top Delegates */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-blue-600" />
              أفضل المندوبين أداءً
            </CardTitle>
          </CardHeader>

          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-right text-sm text-gray-500">
                    <th className="pb-3 font-medium">المندوب</th>
                    <th className="pb-3 font-medium">الشحنات</th>
                    <th className="pb-3 font-medium">إجمالي الإيرادات</th>
                    <th className="pb-3 font-medium">الأداء</th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {topDelegates.map((delegate: any, index: number) => (
                    <tr
                      key={delegate.delegateId || index}
                      className="hover:bg-gray-50"
                    >
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm">
                            {index + 1}
                          </div>

                          <span className="font-medium">
                            {delegate.name && !/EF\/H\(|[\u00C0-\u00FF]/.test(delegate.name)
                              ? delegate.name
                              : `مندوب #${delegate.delegateId}`}
                          </span>
                        </div>
                      </td>

                      <td className="py-3">
                        <Badge
                          variant="outline"
                          className="bg-blue-50 text-blue-700 border-blue-200"
                        >
                          {delegate.completedShipments || 0} شحنة
                        </Badge>
                      </td>

                      <td className="py-3 font-semibold text-gray-900">
                        {formatCurrency(delegate.totalRevenue)}
                      </td>

                      <td className="py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{
                              width: `${Math.min(
                                ((delegate.completedShipments || 0) /
                                  maxDelegateShipments) *
                                  100,
                                100
                              )}%`
                            }}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {topDelegates.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="py-8 text-center text-gray-500"
                      >
                        لا توجد بيانات للمندوبين
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trends */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            اتجاهات الأداء
          </CardTitle>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">

            {trends.map((trend: any, index: number) => {
              const date = trend.date
                ? new Date(trend.date)
                : null;

              const label = date
                ? date.toLocaleDateString('ar-EG', {
                    month: 'short',
                    day: 'numeric'
                  })
                : trend.month || `الشهر ${index + 1}`;

              return (
                <div
                  key={trend.date || trend.month || index}
                  className="flex items-center gap-4"
                >
                  <div className="w-20 text-sm font-medium text-gray-600">
                    {label}
                  </div>

                  <div className="flex-1 space-y-2">

                    {/* Shipments */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-16">
                        الشحنات
                      </span>

                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(
                              ((trend.shipments || 0) /
                                maxTrendShipments) *
                                100,
                              100
                            )}%`
                          }}
                        ></div>
                      </div>

                      <span className="text-xs font-semibold w-12 text-left">
                        {trend.shipments || 0}
                      </span>
                    </div>

                    {/* Revenue */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-16">
                        الإيرادات
                      </span>

                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-green-500 h-3 rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(
                              ((trend.revenue || 0) /
                                maxTrendRevenue) *
                                100,
                              100
                            )}%`
                          }}
                        ></div>
                      </div>

                      <span className="text-xs font-semibold w-24 text-left">
                        {formatCurrency(trend.revenue)}
                      </span>
                    </div>

                  </div>
                </div>
              );
            })}

            {trends.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                لا توجد بيانات كافية للاتجاهات
              </p>
            )}

          </div>
        </CardContent>
      </Card>

    </div>
  );
}
